import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import toast from 'react-hot-toast';
import Fab from '@mui/material/Fab';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import { getEndpoint, getApiUrl } from '../config';

function AuthManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ 
    username: '', 
    password: '' 
  });
  const [passwordForm, setPasswordForm] = useState({ 
    newPassword: '',
    confirmPassword: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('توکن احراز هویت یافت نشد. لطفاً دوباره وارد شوید.');
        window.location.href = '/login';
        return;
      }
      
      const res = await fetch(getApiUrl('/api/auth-management'), {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (res.status === 401) {
        toast.error('توکن منقضی شده است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      } else if (res.status === 403) {
        toast.error('توکن نامعتبر است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('خطا در دریافت کاربران');
      setUsers([]);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این کاربر اطمینان دارید؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiUrl('/api/auth-management')}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      
      if (res.ok) {
        toast.success('کاربر با موفقیت حذف شد');
        fetchUsers();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در حذف کاربر');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error('خطا در حذف کاربر');
    }
  };

  const handleOpenModal = () => {
    setEditUser(null);
    setForm({ username: '', password: '' });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditUser(null);
  };

  const handleOpenPasswordModal = (user) => {
    setEditUser(user);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setPasswordModalOpen(false);
    setEditUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Validation
    if (!form.username || !form.password) {
      toast.error('نام کاربری و رمز عبور الزامی است');
      setSaving(false);
      return;
    }
    
    if (form.username.length < 3) {
      toast.error('نام کاربری باید حداقل 3 کاراکتر باشد');
      setSaving(false);
      return;
    }
    
    if (form.password.length < 6) {
      toast.error('رمز عبور باید حداقل 6 کاراکتر باشد');
      setSaving(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl('/api/auth-management'), {
        method: 'POST',
        headers: { 
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      
      if (res.ok) {
        toast.success('کاربر با موفقیت ایجاد شد');
        fetchUsers();
        handleCloseModal();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در ایجاد کاربر');
      }
    } catch (err) {
      console.error('Error creating user:', err);
      toast.error('خطا در ایجاد کاربر');
    }
    setSaving(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Validation
    if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('رمز عبور جدید و تأیید رمز عبور الزامی است');
      setSaving(false);
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('رمز عبور باید حداقل 6 کاراکتر باشد');
      setSaving(false);
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('رمز عبور جدید و تأیید رمز عبور مطابقت ندارند');
      setSaving(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiUrl('/api/auth-management')}/${editUser.id}/password`, {
        method: 'PUT',
        headers: { 
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newPassword: passwordForm.newPassword })
      });
      
      if (res.ok) {
        toast.success('رمز عبور با موفقیت تغییر یافت');
        handleClosePasswordModal();
      } else {
        const error = await res.json();
        toast.error(error.error || 'خطا در تغییر رمز عبور');
      }
    } catch (err) {
      console.error('Error changing password:', err);
      toast.error('خطا در تغییر رمز عبور');
    }
    setSaving(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Vazir' }}>
          مدیریت کاربران
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{ fontFamily: 'Vazir' }}
        >
          افزودن کاربر جدید
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>نام کاربری</TableCell>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>تاریخ ایجاد</TableCell>
              <TableCell align="center" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} align="center">در حال بارگذاری...</TableCell></TableRow>
            ) : users?.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center">کاربری وجود ندارد.</TableCell></TableRow>
            ) : users?.map(user => (
              <TableRow key={user.id}>
                <TableCell align="center" sx={{ fontFamily: 'Vazir' }}>{user.username}</TableCell>
                <TableCell align="center" sx={{ fontFamily: 'Vazir' }}>{formatDate(user.created_at)}</TableCell>
                <TableCell align="center">
                  <IconButton 
                    color="primary" 
                    onClick={() => handleOpenPasswordModal(user)}
                    title="تغییر رمز عبور"
                  >
                    <LockIcon />
                  </IconButton>
                  <IconButton 
                    color="error" 
                    onClick={() => handleDelete(user.id)}
                    title="حذف کاربر"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Modal افزودن کاربر */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>
          افزودن کاربر جدید
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام کاربری"
              value={form.username}
              onChange={(e) => setForm({...form, username: e.target.value})}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
              helperText="حداقل 3 کاراکتر"
            />
            
            <TextField
              fullWidth
              label="رمز عبور"
              type="password"
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
              helperText="حداقل 6 کاراکتر"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} sx={{ fontFamily: 'Vazir' }}>انصراف</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ fontFamily: 'Vazir' }}>
              {saving ? 'در حال ذخیره...' : 'ایجاد'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Modal تغییر رمز عبور */}
      <Dialog open={passwordModalOpen} onClose={handleClosePasswordModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir' }}>
          تغییر رمز عبور - {editUser?.username}
        </DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="رمز عبور جدید"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
              helperText="حداقل 6 کاراکتر"
            />
            
            <TextField
              fullWidth
              label="تأیید رمز عبور"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
              margin="normal"
              required
              sx={{ fontFamily: 'Vazir' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePasswordModal} sx={{ fontFamily: 'Vazir' }}>انصراف</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={{ fontFamily: 'Vazir' }}>
              {saving ? 'در حال تغییر...' : 'تغییر رمز عبور'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleOpenModal}
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16,
          zIndex: 1000,
          backgroundColor: '#1976d2',
          '&:hover': {
            backgroundColor: '#1565c0',
          }
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
}

export default AuthManagement;
